/**
 * @file CityAtAGlance.jsx
 * @description A short, dated, sourced fact box under a city page's hero (owner 2026-10-23: Miami RBT first, then
 *   every city page). Each line is one fact a search engine or an AI assistant can quote as is, with its source
 *   linked. Lines are built from data the page already has, never typed per page:
 *   - pay: the page's own pay summary, linked to its source (seoAreas payByRole.<role>.source, checked by
 *     scripts/lib/seoPageGuards paySourceIssues). No linkable pay source = no box at all, and the page keeps its
 *     "Typical pay here" card.
 *   - credential (RBT pages): the state's Medicaid RBT rule, when the states table has it with a source and the
 *     page doesn't already cover funding in its local facts.
 *   - workforce: the state's BACB counts (states table, one source of truth), rounded to the hundred; skipped when
 *     the page already has its own workforce fact.
 *   - employment: W2 or 1099 shown on every case (template line).
 *   Only the pay line is on by default. The other three read the same on every page of a state, and on the
 *   Florida RBT pages they pulled cross-city uniqueness under the 40% bar (scripts/lib/seoUniqueness.js, a build gate since
 *   2026-10-23), so a page opts in with seoAreas glanceExtras.<role> (Miami RBT, the one page that has them).
 *   "Updated" is the area's content date from the sitemap lastmod engine (src/data/seoLastmod.json), so the page and
 *   the sitemap always agree.
 * @module components/cityRole/CityAtAGlance
 */
import { useTranslation } from "react-i18next";
import { T } from "../../theme";
import { DollarIcon, ShieldIcon, UsersIcon, BriefcaseIcon } from "../Icons";
import { STATES, STATES_SOURCE } from "../../data/seoLanding";
import seoLastmod from "../../data/seoLastmod.json";

const ICONS = { pay: DollarIcon, credential: ShieldIcon, workforce: UsersIcon, employment: BriefcaseIcon };

const round100 = (n) => (Math.round(Number(n) / 100) * 100).toLocaleString("en-US");

/** True when this page gets the box: it needs a sourced pay line. */
export function hasGlance(entry) {
  return !!(entry && entry.paySource && entry.paySource.url && entry.paySummaryKey);
}

/** "October 2026" / "octubre de 2026" from a YYYY-MM-DD content date; null when there is none. */
export function glanceMonth(date, isEs) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date || "")) return null;
  return new Intl.DateTimeFormat(isEs ? "es-US" : "en-US", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${date}T12:00:00Z`));
}

/** The lines this page shows, each { kind, text, cite, url }; [] when the page gets no box. Pure, for tests. */
export function glanceLines(entry, t, isEs = false) {
  if (!hasGlance(entry)) return [];
  const state = STATES[entry.state] || {};
  const facts = entry.localFactKeys || {};
  const extras = new Set(entry.glanceExtras || []);
  const stateName = (isEs && state.nameEs) || state.name || entry.stateFull;
  const lines = [];

  const src = entry.paySource;
  // The page's pay sentence word for word, with no "Typical pay:" label: its own lead ("RBT pay in Charlotte: ...")
  // is the part that differs between cities, and trimming it cost pages cross-city uniqueness (2026-10-23).
  lines.push({ kind: "pay", text: t(entry.paySummaryKey), cite: t(src.citeKey || `cityRole.glanceCite.${src.by}`), url: src.url });

  const rule = state.medicaidRbtSource;
  if (extras.has("credential") && entry.roleKey === "rbt" && state.medicaidRequiresRbt && state.medicaidRbtSince && rule && rule.url && !facts.funding) {
    lines.push({
      kind: "credential",
      text: t("cityRole.glance.credential", { medicaid: (isEs && state.medicaidNameEs) || state.medicaidName, year: state.medicaidRbtSince }),
      cite: (isEs && rule.citeEs) || rule.cite,
      url: rule.url,
    });
  }

  if (extras.has("workforce") && state.bacb && STATES_SOURCE && STATES_SOURCE.url && !facts.workforce) {
    const vars = { rbt: round100(state.bacb.rbt), bcba: round100(state.bacb.bcba), state: stateName };
    lines.push({
      kind: "workforce",
      text: state.rank === 1 ? t("cityRole.glance.workforceTop", vars) : t("cityRole.glance.workforce", vars),
      cite: t("cityRole.glanceCite.bacb"),
      url: STATES_SOURCE.url,
    });
  }

  if (extras.has("employment")) lines.push({ kind: "employment", text: t("cityRole.glance.employment"), cite: null, url: null });
  return lines;
}

export default function CityAtAGlance({ entry, d, isMobile, isEs }) {
  const { t } = useTranslation();
  const lines = glanceLines(entry, t, isEs);
  if (!lines.length) return null;
  const month = glanceMonth(seoLastmod.areas[entry.areaKey]?.date, isEs);

  return (
    <section data-testid="city-at-a-glance" style={{ padding: isMobile ? "28px 18px 0" : "40px 48px 0" }}>
      <div style={{ maxWidth: 980, margin: "0 auto", background: T.indigoLight, border: "1px solid rgba(79,70,229,0.18)", borderRadius: 16, padding: isMobile ? "20px 18px" : "26px 30px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", justifyContent: "space-between", gap: "4px 16px", marginBottom: 14 }}>
          <h2 style={{ fontSize: isMobile ? 19 : 22, fontWeight: 900, color: T.text, margin: 0, letterSpacing: "-0.02em" }}>
            {t("cityRole.glanceTitle", { role: d.roleSingular, area: d.area })}
          </h2>
          {month && <span style={{ fontSize: 13, color: T.textMid, fontWeight: 600 }}>{t("cityRole.glanceUpdated", { date: month })}</span>}
        </div>
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: isMobile ? 14 : 12 }}>
          {lines.map((l) => {
            const Icon = ICONS[l.kind];
            return (
              <li key={l.kind} data-testid={`glance-${l.kind}`} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <span style={{ width: 32, height: 32, borderRadius: 9, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon size={16} color={T.indigo} />
                </span>
                <div style={{ minWidth: 0, fontSize: 15, lineHeight: 1.55, color: T.textMid, paddingTop: 5 }}>
                  {l.kind !== "pay" && (
                    <>
                      <strong style={{ color: T.text, fontWeight: 800 }}>
                        {t(`cityRole.glanceLabel.${l.kind}`, { state: d.stateName })}:
                      </strong>{" "}
                    </>
                  )}
                  {l.text}
                  {l.cite && l.url && (
                    <>
                      {" "}
                      <a href={l.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: T.indigoDark, fontWeight: 600, whiteSpace: "nowrap" }}>({l.cite})</a>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
